import type { LandingIconName } from './icons';

type Anchor = `#${string}`;
type LandingHref = Anchor | `mailto:${string}`;

export type NavLink = Readonly<{
  label: string;
  href: LandingHref;
}>;

type LandingHeadingCopy =
  | Readonly<{
      heading: string;
      headingLead?: never;
      headingAccent?: never;
    }>
  | Readonly<{
      heading?: never;
      headingLead: string;
      headingAccent: string;
    }>;

export type LandingSectionCopy = Readonly<{
  id?: string;
  eyebrow: string;
  body?: string;
  ctas?: readonly Readonly<{ label: string; href: Anchor; variant: 'primary' | 'ghost' }>[];
}> &
  LandingHeadingCopy;

export type LandingCard = Readonly<{
  icon: LandingIconName;
  title: string;
  body: string;
}>;

export type PartnerTile = Readonly<{
  tag: string;
  title: string;
  body: string;
}>;

export type IntelligencePoint = Readonly<{
  title: string;
  body: string;
}>;

export type SignalBar = Readonly<{
  label: string;
  value: number;
  variant?: 'violet';
}>;

export type FooterLinkColumn = Readonly<{
  title: string;
  links: readonly NavLink[];
}>;

export const LANDING_NAV_LINKS = [
  { label: 'Platform', href: '#platform' },
  { label: 'App', href: '#app' },
  { label: 'The Loop', href: '#loop' },
  { label: 'Research', href: '#research' },
] as const satisfies readonly NavLink[];

export const LANDING_MOBILE_NAV_LINKS = [
  { label: 'Platform', href: '#platform' },
  { label: 'App', href: '#app' },
  { label: 'The Loop', href: '#loop' },
  { label: 'Research', href: '#research' },
  { label: 'Request a Briefing', href: '#contact' },
] as const satisfies readonly NavLink[];

export const LANDING_SECTION_ORDER = [
  'platform',
  'app',
  'connective',
  'loop',
  'modules',
  'intelligence',
  'research',
  'contact',
] as const;

export const LANDING_COPY = {
  meta: {
    title: 'RegenLoop.ai | Closed-loop outcomes intelligence',
    description:
      'Closed-loop outcomes intelligence for regenerative medicine teams connecting clinics, labs, sponsors, and patients.',
    ogTitle: 'RegenLoop.ai | Closed-loop outcomes intelligence',
    ogDescription:
      'Closed-loop outcomes intelligence for regenerative medicine teams connecting clinics, labs, sponsors, and patients.',
  },
  brand: {
    prefix: 'Regen',
    core: 'Loop',
    suffix: '.ai',
    homeLabel: 'RegenLoop home',
    positioning: 'Closed-loop outcomes intelligence for regenerative medicine.',
  },
  hero: {
    eyebrow: 'Outcomes intelligence platform',
    headingLead: 'Closed-loop intelligence for the teams advancing',
    headingAccent: 'stem cell research.',
    body: 'RegenLoop connects everyone in the loop — the labs, clinics, and sponsors that procure stem cell lines and treat patients with them — so the effectiveness of every protocol can be measured, compared, and improved.',
    ctas: [
      { label: 'Request a Briefing', href: '#contact', variant: 'primary' },
      { label: 'See the Loop', href: '#loop', variant: 'ghost' },
    ],
  },
  problem: {
    id: 'problem',
    eyebrow: 'The gap',
    heading: 'Regenerative medicine is moving faster than the systems built to measure it.',
    body: 'Most clinics, physicians, labs, and researchers still rely on fragmented follow-ups, disconnected spreadsheets, subjective reports, and inconsistent outcome tracking. RegenLoop brings the process into one closed loop.',
  },
  solution: {
    id: 'platform',
    eyebrow: 'What RegenLoop does',
    heading: 'One system for the entire outcomes workflow.',
    body: 'From the first follow-up to efficacy analysis, every signal is captured, structured, and connected — built around how regenerative medicine is actually practiced and studied.',
  },
  app: {
    id: 'app',
    eyebrow: "In the clinician's hand",
    heading: 'A mobile app, made for clinicians.',
    body: "The whole closed loop — production, QC release, lot lineage, inventory across sites — lives in the desktop console for the lab. The treating clinician carries something narrower: a native iOS & Android app holding their patients, the sessions they administer, and the outcomes coming back. Just what point-of-care needs — nothing it doesn't.",
  },
  connective: {
    id: 'connective',
    eyebrow: 'The connective layer',
    heading: 'Three parties. One layer between them.',
    body: 'Labs, physicians, and patients each hold one piece of the picture. RegenLoop is the layer that sits between all three — capturing structured data at every handoff and feeding the intelligence back to sharpen the next line.',
  },
  loop: {
    id: 'loop',
    eyebrow: 'The RegenLoop',
    heading: 'Six stages. One loop that keeps learning.',
    body: 'Labs source and produce the stem cell lines. Physicians acquire and administer them in practice. Patients generate the outcomes — which flow back through RegenLoop to sharpen the next line sourced, produced, and prescribed.',
  },
  modules: {
    id: 'modules',
    eyebrow: 'Platform modules',
    heading: 'Modular by design. Connected by default.',
    body: 'Adopt the pieces you need today and add the rest as you scale — every module shares one outcomes model underneath.',
  },
  intelligence: {
    id: 'intelligence',
    eyebrow: 'Intelligence layer',
    heading: 'AI-assisted analysis that surfaces efficacy signals.',
  },
  research: {
    id: 'research',
    eyebrow: 'Responsible by design',
    heading: 'Built for serious clinical and research environments.',
    body: 'RegenLoop is designed around structured data collection, privacy-aware workflows, consent management, and research-grade reporting — credible outcomes intelligence without compromising patient trust.',
  },
  partners: {
    id: 'partners',
    eyebrow: "Who it's for",
    heading: 'Everyone with a stake in the outcome.',
    body: 'Clinicians, researchers, labs, distributors, institutions, and funders — everyone who believes regenerative medicine needs better evidence infrastructure.',
  },
  finalCta: {
    eyebrow: 'Close the loop',
    heading: 'Turn every outcome into evidence.',
    body: "Let's talk about how RegenLoop fits your clinic, lab, study, or portfolio.",
    ctas: [{ label: 'Start the Conversation', href: '#contact', variant: 'primary' }],
  },
  contact: {
    id: 'contact',
    eyebrow: 'Request a briefing',
    heading: "Tell us what you're working on.",
    body: "Tell us a little about your work and what you're exploring. Our team will follow up to schedule a briefing.",
  },
} as const satisfies Record<
  string,
  | LandingSectionCopy
  | Record<string, string>
  | {
      readonly prefix: string;
      readonly core: string;
      readonly suffix: string;
      readonly homeLabel: string;
      readonly positioning: string;
    }
>;

export const PROBLEM_FRAGMENTS = [
  'Patient follow-up scattered across calls, texts, and memory',
  'Clinical outcomes trapped in disconnected spreadsheets',
  'Lab & product variables never linked to results',
  'Protocols and cohorts tracked inconsistently',
  'No feedback loop from outcomes back into practice',
] as const;

export const SOLUTION_CARDS = [
  {
    icon: 'bell',
    title: 'Patient follow-up automation',
    body: 'Scheduled questionnaires, reminders, and check-ins keep longitudinal data flowing without manual chasing.',
  },
  {
    icon: 'outcomes',
    title: 'Clinical outcome tracking',
    body: 'Capture objective and subjective results over time, structured the same way for every patient.',
  },
  {
    icon: 'lab',
    title: 'Lab / product intelligence',
    body: 'Connect outcomes to product variables, lot numbers, and preparation details.',
  },
  {
    icon: 'network',
    title: 'Physician workflow support',
    body: 'Outside providers participate in structured, consistent data collection — not free-text notes.',
  },
  {
    icon: 'dashboard',
    title: 'Research dashboard',
    body: 'Organize data for studies, protocols, cohorts, enrollment, and reporting.',
  },
  {
    icon: 'analysis',
    title: 'AI-assisted analysis',
    body: 'Identify efficacy patterns and outcome signals across the full dataset.',
  },
] as const satisfies readonly LandingCard[];

export const MODULE_CARDS = [
  {
    icon: 'bell',
    title: 'Patient Follow-Up',
    body: 'Automated questionnaires, reminders, and response tracking.',
  },
  {
    icon: 'outcomes',
    title: 'Outcomes Registry',
    body: 'Longitudinal patient-reported and clinical outcomes.',
  },
  {
    icon: 'lab',
    title: 'Lot & Product Tracking',
    body: 'Connect biological product variables to patient outcomes.',
  },
  {
    icon: 'dashboard',
    title: 'Study Management',
    body: 'Protocols, cohorts, enrollment, milestones, and data exports.',
  },
  {
    icon: 'network',
    title: 'Provider Network',
    body: 'Multi-provider participation and structured data submission.',
  },
  {
    icon: 'analysis',
    title: 'Intelligence Layer',
    body: 'AI-assisted analysis, reporting, and efficacy signals.',
  },
] as const satisfies readonly LandingCard[];

export const INTELLIGENCE_POINTS = [
  {
    title: 'Cohort & response patterns',
    body: 'Group patients by protocol, indication, or product and compare longitudinal response — automatically.',
  },
  {
    title: 'Lot & product correlation',
    body: 'Connect outcomes to lot numbers, preparation, and biological product variables to see what moves results.',
  },
  {
    title: 'Efficacy signal detection',
    body: 'Identify what is working, for whom, when, and why — surfaced as signals, not promises.',
  },
] as const satisfies readonly IntelligencePoint[];

export const SIGNAL_PANEL = {
  title: 'Efficacy signals — cohort view',
  status: 'Analyzing',
} as const;

export const SIGNAL_BARS = [
  { label: 'Cohort A', value: 86 },
  { label: 'Cohort B', value: 71 },
  { label: 'Lot 4471', value: 78, variant: 'violet' },
  { label: 'Lot 4482', value: 54, variant: 'violet' },
  { label: '6-mo PRO', value: 64 },
] as const satisfies readonly SignalBar[];

export const RESEARCH_TRUST_CARDS = [
  {
    icon: 'lock',
    title: 'Role-based access',
    body: 'Secure, permissioned handling of sensitive clinical data.',
  },
  {
    icon: 'shieldck',
    title: 'Consent management',
    body: 'Consent captured and tracked as part of every workflow.',
  },
  {
    icon: 'shield',
    title: 'Privacy-aware design',
    body: 'HIPAA-aware practices and secure data handling throughout.',
  },
  {
    icon: 'report',
    title: 'Research-grade reports',
    body: 'Structured exports built for studies and oversight.',
  },
] as const satisfies readonly LandingCard[];

export const PARTNER_TILES = [
  {
    tag: 'Investors',
    title: 'Fund the infrastructure layer',
    body: 'Back the operational and research backbone of an emerging clinical market.',
  },
  {
    tag: 'Clinicians',
    title: 'Track outcomes in practice',
    body: 'Treat patients and capture standardized outcomes — in a study or in standard care.',
  },
  {
    tag: 'Labs',
    title: 'Connect product to outcome',
    body: 'Understand how product variables relate to longitudinal results.',
  },
  {
    tag: 'Institutions',
    title: 'Explore data partnerships',
    body: 'Open research, grant, and data-analysis collaborations.',
  },
] as const satisfies readonly PartnerTile[];

export const CONTACT_DATA = {
  email: 'partnerships@regenloop.ai',
  privacyLine: 'Privacy-aware, consent-based workflows',
  form: {
    fields: {
      name: { label: 'Name', required: true, placeholder: 'Full name' },
      email: { label: 'Email', required: true, placeholder: 'you@org.com' },
      organization: { label: 'Organization', required: false, placeholder: 'Clinic, lab, fund...' },
      role: { label: 'Role', required: false, placeholder: 'Your role' },
      interest: { label: 'Interest', required: false, placeholder: 'Select an area…' },
      message: { label: 'Message', required: false, placeholder: 'What are you exploring?' },
    },
    interestOptions: [
      'Investment / funding',
      'Clinical partnership',
      'Research collaboration',
      'Lab / product partnership',
      'Government or institutional opportunity',
      'General inquiry',
    ],
    submitLabel: 'Request Briefing',
    note: 'Your information is handled with privacy-aware, consent-based practices.',
    success: {
      title: 'Thank you.',
      body: 'Our team will follow up shortly to schedule your briefing.',
    },
  },
} as const;

export const FOOTER_LINK_COLUMNS = [
  {
    title: 'Platform',
    links: [
      { label: 'Solution', href: '#platform' },
      { label: 'Modules', href: '#modules' },
      { label: 'The Loop', href: '#loop' },
      { label: 'Intelligence', href: '#intelligence' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Research', href: '#research' },
      { label: 'Partners', href: '#partners' },
      { label: 'Request a Briefing', href: '#contact' },
    ],
  },
  {
    title: 'Contact',
    links: [
      { label: 'partnerships@regenloop.ai', href: 'mailto:partnerships@regenloop.ai' },
      { label: 'Privacy', href: '#' },
      { label: 'Terms', href: '#' },
    ],
  },
] as const satisfies readonly FooterLinkColumn[];

export const FOOTER_COPY = {
  copyright: '© 2026 RegenLoop.ai — regenloop.ai',
  legalLine: 'Outcomes-data & research-support platform. No medical or efficacy claims.',
} as const;

export const STICKY_CTA = {
  label: 'Request a Briefing',
  href: '#contact',
} as const satisfies NavLink;
